import type { PresignedUpload } from '@foundation/shared';
import { api, tokenStore } from './api';
import { sha256Hex, shrinkImage, type Coords } from './image';

/**
 * อัปโหลดรูปเข้า R2
 *
 * รองรับ 2 โหมดที่ backend เลือกให้:
 *   presigned — PUT ตรงเข้า R2 (production)
 *   worker    — PUT ผ่าน Worker (ตอนพัฒนาที่ยังไม่ได้ตั้ง R2 API credentials)
 *
 * รูปที่อัปโหลดสำเร็จแล้วจะไม่หายแม้ฟอร์มยังไม่ถูก submit — key ถูกเก็บใน draft
 * ทำให้เจ้าหน้าที่กลับมาทำต่อได้เมื่อเน็ตหลุดกลางคัน
 */

export interface UploadedPhoto {
  key: string;
  previewUrl: string;
  width: number;
  height: number;
  sizeBytes: number;
  sha256: string;
  takenAt: string;
  lat?: number;
  lng?: number;
}

interface PresignResponse {
  uploads: PresignedUpload[];
  maxSizeBytes: number;
  mode: 'presigned' | 'worker';
}

export async function uploadPhoto(
  file: File | Blob,
  opts: { refId: string; coords?: Coords | null },
): Promise<UploadedPhoto> {
  const image = await shrinkImage(file);

  const presign = await api<PresignResponse>('/uploads/presign', {
    method: 'POST',
    body: { purpose: 'visit_photo', refId: opts.refId, count: 1, contentType: 'image/jpeg' },
  });

  const target = presign.uploads[0];
  if (!target) throw new Error('ขอที่อยู่สำหรับอัปโหลดไม่สำเร็จ');

  const headers: Record<string, string> = { 'content-type': 'image/jpeg' };
  // โหมด worker ต้องแนบ token เพราะยิงเข้า API ของเราเอง
  if (presign.mode === 'worker') {
    const token = tokenStore.get();
    if (token) headers.authorization = `Bearer ${token}`;
  }

  const res = await fetch(target.url, { method: 'PUT', headers, body: image.blob });
  if (!res.ok) {
    URL.revokeObjectURL(image.previewUrl);
    throw new Error(`อัปโหลดรูปไม่สำเร็จ (${res.status})`);
  }

  return {
    key: target.key,
    previewUrl: image.previewUrl,
    width: image.width,
    height: image.height,
    sizeBytes: image.sizeBytes,
    sha256: await sha256Hex(image.blob),
    takenAt: new Date().toISOString(),
    ...(opts.coords ? { lat: opts.coords.lat, lng: opts.coords.lng } : {}),
  };
}

/* ------------------------------------------------------------------ draft */

const DRAFT_PREFIX = 'foundation.visit.draft.';

export interface VisitDraft {
  formData: Record<string, unknown>;
  /** จัดกลุ่มตาม fieldKey — ต้องเก็บโครงนี้ ไม่งั้นกู้ draft แล้วไม่รู้ว่ารูปไหนของฟิลด์ไหน */
  photos: Record<string, UploadedPhoto[]>;
  summary?: string;
  outcome?: string;
  locationReason?: string;
  savedAt: string;
}

/**
 * เก็บ draft ไว้ในเครื่อง — เจ้าหน้าที่กรอกฟอร์มยาวกลางแดด เน็ตหลุดบ่อย
 * ถ้าไม่เก็บ ข้อมูลที่กรอกไป 10 นาทีจะหายทั้งหมด
 */
export function saveDraft(visitId: string, draft: Omit<VisitDraft, 'savedAt'>): void {
  try {
    localStorage.setItem(
      DRAFT_PREFIX + visitId,
      JSON.stringify({ ...draft, savedAt: new Date().toISOString() }),
    );
  } catch {
    // localStorage เต็มหรือถูกปิด — ไม่ใช่เหตุให้หยุดทำงาน
  }
}

export function loadDraft(visitId: string): VisitDraft | null {
  try {
    const raw = localStorage.getItem(DRAFT_PREFIX + visitId);
    return raw ? (JSON.parse(raw) as VisitDraft) : null;
  } catch {
    return null;
  }
}

export function clearDraft(visitId: string): void {
  try {
    localStorage.removeItem(DRAFT_PREFIX + visitId);
  } catch {
    /* ไม่สำคัญ */
  }
}
